/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useKPIContext } from '@/providers/KpiProvider';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import Icons from '@/ux-common/src/icons/icons';
import { EvaluateTemplate } from '@/ux-common/src/types/api';
import { MAX_EVAL_JOBS_COUNT } from '@/ux-common/src/utils/constants';
import { getActiveLocale } from '@/ux-common/src/utils/locale';
import { LucideChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PostEvalsJobsBodyType } from 'types/api-data';
import { OptionsType, OptionsTypes } from 'types/multi-select';
import { IPrompt, TIdSpecificEvalConfig } from 'types/prompt';

import { cn } from '@/lib/utils';
import { useEvals } from '@/hooks/useEvals';
import { usePrompts } from '@/hooks/usePrompts';
import { useTraces } from '@/hooks/useTraces';

import { EnhancedMultiSelect } from '../combobox';
import { CustomButton } from '../common';
import { FACT_NAME_LABEL_MAP } from '../display-page/fact-name-label-map';
import { System } from '../icons';
import { NotFoundMessage } from '../not-found';
import { TreeDataItem } from '../treeview-dropdown';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Separator } from '../ui/separator';

type EvalModalProps = {
  inferTitle: 'inferences' | 'traces' | 'conversations';
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  appName: string | undefined;
  breakdownFilter?: TreeDataItem | null | string;
  selectedPrompt?: IPrompt[];
  placeholder?: string;
  traceId?: string | null;
  dateRange?: any;
  source?: 'conversation' | 'trace';
  setEvalConfig?: React.Dispatch<
    React.SetStateAction<TIdSpecificEvalConfig | null>
  >;
  evalFilters?: string | null;
  isSearchByIdTab?: boolean;
  workflowName?: string;
  // start/end of the trace being evaluated; takes precedence over the date range
  traceStartTime?: string | null;
  traceEndTime?: string | null;
};

export const EvalModal = ({
  open,
  setOpen,
  inferTitle,
  breakdownFilter,
  placeholder,
  traceId,
  selectedPrompt,
  appName,
  dateRange,
  source,
  setEvalConfig,
  evalFilters,
  isSearchByIdTab,
  workflowName,
  traceStartTime,
  traceEndTime,
}: EvalModalProps) => {
  const tracesHookResult = useTraces();
  const promptsHookResult = usePrompts();
  const kpiHookResult = useKPIContext();

  const promptsState = promptsHookResult?.state;

  // When evaluating a specific trace, use that trace's own time range.
  // Otherwise, for search-by-id evaluation don't send time range.
  const hasTraceTimeRange = !!(traceStartTime && traceEndTime);
  const startTime = hasTraceTimeRange
    ? traceStartTime
    : isSearchByIdTab
      ? undefined
      : tracesHookResult?.filters
        ? tracesHookResult?.filters?.timeRange?.startTime
        : promptsState
          ? promptsState?.queryParams?.start_time
          : dateRange?.from?.toISOString();

  const endTime = hasTraceTimeRange
    ? traceEndTime
    : isSearchByIdTab
      ? undefined
      : tracesHookResult?.filters
        ? tracesHookResult?.filters?.timeRange?.endTime
        : promptsState
          ? promptsState?.queryParams?.end_time
          : dateRange?.to?.toISOString();

  // For search-by-id evaluation don't send breakdown
  const selectedBreakdown = isSearchByIdTab
    ? ''
    : typeof breakdownFilter === 'string'
      ? breakdownFilter
      : breakdownFilter?.id || '';

  // const [radioSelected, setRadioSelected] = useState('skip');
  const [selectCriteria, setSelectCriteria] = useState<OptionsTypes>([]);
  const [runningJobsCount, setRunningJobsCount] = useState(0);
  const [staticTemplates, setStaticTemplates] = useState<EvaluateTemplate[]>(
    []
  );
  const [staticTemplateLoading, setStaticTemplateLoading] = useState(false);
  const {
    state: { evalJobs },
    evaluateActions: { fetchEvalsJobs, createEvalsJobs },
  } = useEvals();

  const { messages } = useLanguage();
  const langData = messages?.General;
  const evalsData = messages?.Evaluate;

  const title = {
    inferences: messages?.General?.inferences,
    traces: messages?.CompTabs.traces,
    conversations: messages?.General?.conversations,
  };

  const isBreakdownDefault = !selectedBreakdown.includes('.');

  // The fact_name always reflects the fact type of the items being evaluated
  // (inferTitle), for both apps and workflows. Trace-level entry points (e.g. the
  // insights KPI button) pass 'traces'; span-level entry points (the Prompts view)
  // pass 'inferences' — so we must not hardcode 'traces' for workflows here.
  const evalFactName = inferTitle;

  // // TODO #1225 Values should come from en.json
  // const radioBtnSpecs = [
  //   {
  //     id: 1,
  //     value: 'skip',
  //     label: 'Skip',
  //   },
  //   {
  //     id: 2,
  //     value: 'update',
  //     label: 'Update',
  //   },
  // ];

  const evaluateOptions: OptionsTypes = staticTemplates?.map(
    (template: EvaluateTemplate, id: number) => ({
      id: id + 1,
      label: template.name,
      value: template.name,
    })
  );

  const handleCriteriaChange = (selectedValues: any) => {
    const selected = evaluateOptions.filter((item) =>
      selectedValues.includes(item.value)
    );
    setSelectCriteria(selected);
  };

  const handleDeleteTemplate = (option: OptionsType) => {
    const newOptions: OptionsTypes = selectCriteria.filter(
      (item) => item.value !== option.label
    );
    setSelectCriteria(newOptions);
  };

  //when we evaluate from a specific ID, get jobs status api doesn't include this info, hence we don't call it and directly call query api.
  const onSubmit = async () => {
    setEvalConfig && setEvalConfig(null);
    const promptTraceId =
      selectedPrompt?.length === 1 && selectedPrompt[0].trace_id;
    const workflowNames =
      workflowName ||
      tracesHookResult?.filters?.workflowNames ||
      promptsState?.queryParams?.workflow_names ||
      kpiHookResult?.workflowNames;
    const queryParams = Object.fromEntries(
      Object.entries({
        ...(workflowNames
          ? { workflow_name: workflowNames }
          : { app_name: appName }),
        start_time: startTime,
        end_time: endTime,
        breakdown_filter: selectedBreakdown,
        fact_name: evalFactName,
        conversation_id:
          source === 'conversation' && (traceId || promptTraceId),
        trace_id: source === 'trace' && (traceId || promptTraceId), //for span eval also, we take trace_id. unused one will be filtered between trace_id/conversation_id.
        eval_filters: evalFilters,
      }).filter(([, value]) => value != null && value !== '' && value !== false)
    );

    setOpen(false);
    // Trigger evaluation started status
    window.dispatchEvent(new Event('evaluationStarted'));

    toast.success(`${messages?.Evaluation?.evaluation_success_title}`, {
      description: `${messages?.Evaluation?.evaluation_success_description}`,
    });

    // Call createEvalsJobs for each selected criteria
    Promise.allSettled(
      selectCriteria.map((criteria) => {
        const body: PostEvalsJobsBodyType = {
          template_name: criteria.value,
        };
        return createEvalsJobs(queryParams, body);
      })
    ).then(() => {
      // check if we had trace_id or conversation_id, if not, we fetch evalsJobs
      if (!traceId && !promptTraceId) {
        fetchEvalsJobs(appName, workflowName);
      }
    });
  };

  // fetch templates in static catalogue mode locally (instead of dispatching
  // to the global context) to avoid overwriting the parent page's (Insights,
  // Traces, or Prompts) discovery-mode templates, and load active jobs.
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setStaticTemplateLoading(true);
        const res = await request.get(
          {
            endpoint: 'eval/templates',
            queryParams: { fact_name: evalFactName },
          },
          undefined,
          undefined,
          true
        );
        const staticTemplatesList = res[0]?.templates || [];
        setStaticTemplates(staticTemplatesList);
      } catch (error) {
        console.error('Error fetching templates in modal:', error);
      } finally {
        setStaticTemplateLoading(false);
      }
    };

    fetchTemplates();
    fetchEvalsJobs(appName, workflowName);
  }, [evalFactName, appName, workflowName]);

  useEffect(() => {
    const jobsCount = evalJobs.filter(
      (job: any) => job.status === 'RUNNING'
    ).length;

    setRunningJobsCount(jobsCount);
  }, [evalJobs]);

  // function will return a formatted date string e.g 01 Sep 2024.
  const formattedDate = (dateTime: string) => {
    const date = new Date(dateTime);
    // return empty string if date is invalid
    if (isNaN(date.getTime())) return '';
    const activeLocale = getActiveLocale();
    return date.toLocaleDateString(activeLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-[976px] gap-0 border-link-active bg-gradient-sky-dark"
        aria-describedby={undefined}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="border-b border-line-primary pb-5 text-2xl">
            {evalsData?.modal_title} {title[inferTitle]}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {traceId && (
            <p className="flex items-center gap-1.5 text-body text-body-subtle">
              {System.InfoIcon('size-[18px]')}
              {evalsData?.trace_id_info}{' '}
              {source === 'conversation'
                ? langData?.conversation_id
                : FACT_NAME_LABEL_MAP[inferTitle]?.idLabel ||
                  langData?.trace_id}{' '}
              {traceId}
            </p>
          )}

          {selectedPrompt?.length === 1 && (
            <div className="rounded border border-[#40565A] bg-canvas-background-primary px-4 py-3">
              <p className="flex items-center gap-1.5 text-body text-body-subtle">
                {System.InfoIcon('size-[18px]')}
                {evalsData?.inference_info}
              </p>
              {selectedPrompt.map((item: IPrompt) => (
                <div
                  key={item.trace_id}
                  className="ml-6 space-y-1 text-body text-body-primary"
                >
                  <p>
                    {langData?.trace_id}: {item.trace_id}
                  </p>
                  <p>
                    {langData?.span_id}: {item.span_id}
                  </p>
                </div>
              ))}
            </div>
          )}

          {!isBreakdownDefault && (
            <div className="space-y-0.5 text-body text-body-primary">
              <p className="text-form-input-text text-form-input-rest">
                {evalsData?.breakdown}
              </p>
              <div className="flex items-center">
                {placeholder?.split('>').map((item, index, array) => (
                  <React.Fragment key={item}>
                    <span>{item}</span>
                    {index < array.length - 1 && (
                      <LucideChevronRight className="size-4 text-form-input-disabled" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          {/* For search-by-id evaluation, don't display time range */}
          {!isSearchByIdTab && (
            <div className="flex flex-col gap-y-0.5">
              <p className="text-form-input-text text-form-input-rest">
                {evalsData?.during}
              </p>
              <p className="text-body text-body-primary">
                {(startTime === null && endTime === null) ||
                (dateRange && dateRange.from === null && dateRange.to === null)
                  ? langData?.default_observation_window
                  : `${formattedDate(startTime)} - ${formattedDate(endTime)}`}
              </p>
            </div>
          )}

          <div>
            <p className="text-form-input-text text-form-input-rest">
              {evalsData?.criteria}
            </p>
            <div className="space-y-1">
              <EnhancedMultiSelect
                defaultValue={selectCriteria?.map((item) => item.value)}
                showPills={false}
                loading={staticTemplateLoading}
                options={evaluateOptions}
                onValueChange={handleCriteriaChange}
                placeholder={langData?.all}
                inputPlaceholder={
                  langData?.select_evaluation_criteria_placeholder
                }
                hasDelete={true}
                maxOptions={MAX_EVAL_JOBS_COUNT - runningJobsCount}
              />
              <div
                className={cn(
                  'h-[124px] overflow-y-auto rounded border border-line-secondary',
                  selectCriteria.length && 'border-line-primary'
                )}
              >
                {selectCriteria.length > 0 ? (
                  <ul className="list-none">
                    {selectCriteria?.map((criteria) => (
                      <li
                        key={criteria.value}
                        className={cn(
                          'flex items-center justify-between border-b border-b-line-secondary pl-4 pr-3 text-sm'
                        )}
                      >
                        <div className="flex items-center gap-x-2 text-form-input-text text-white">
                          <span className="py-2.5">{criteria.label}</span>
                        </div>
                        <button onClick={() => handleDeleteTemplate(criteria)}>
                          {Icons.Close(
                            'size-5 stroke-1 cursor-pointer text-ocean'
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center">
                    <NotFoundMessage
                      title={langData?.no_evaluation_criteria ?? ''}
                      message={langData?.no_evaluation_criteria_subtitle}
                      outerDivClassName="gap-1 my-[0px]"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* <div className="space-y-1">
            <p className="text-form-input-text text-form-input-rest">
              {evalsData?.manage_existing_values}
            </p>
            <RadioGroup
              defaultValue="skip"
              className="px-0.5 py-2"
              onValueChange={(value) => setRadioSelected(value)}
            >
              {radioBtnSpecs.map((radio) => (
                <div key={radio.id} className="flex items-center space-x-2">
                  <RadioGroupItem
                    className={cn(
                      'bg-canvas-background-primary',
                      radioSelected === radio.value &&
                        'border-none bg-fill-button-rest'
                    )}
                    value={radio.value ?? ''}
                    id={radio.value ?? ''}
                  />
                  <Label
                    className="text-form-input-text text-body-primary"
                    htmlFor={radio.value}
                  >
                    {radio.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            {!traceId ? (
              <p className="text-form-input-text text-form-input-rest">
                {evalsData?.runs_on}{' '}
                <Input
                  type="number"
                  min={1}
                  max={100}
                  className="inline-block w-16 rounded-sm text-form-input-text [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />{' '}
                {evalsData?.percent_of_traces}
              </p>
            ) : null}
          </div> */}
        </div>
        <Separator className="mt-6 bg-line-primary" />
        <DialogFooter className="mt-5 sm:justify-start">
          <CustomButton
            // type="submit"
            type="button" // switch to type submit when incorporating the radio and the percentage
            onClick={() => onSubmit()}
            customSize="regular"
            customVariant="primary"
            disabled={staticTemplateLoading || selectCriteria.length === 0}
          >
            {langData?.evaluate}
          </CustomButton>
          <DialogClose asChild>
            <CustomButton
              customSize="regular"
              customVariant="secondary"
              onClick={() => setEvalConfig && setEvalConfig(null)}
            >
              {langData?.cancel}
            </CustomButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
